import type { LanguageContent } from './types.js';

// Portuguese words will be loaded from external source to avoid bundling
let portugueseWords: any[] | null = null;

async function loadPortugueseWords(): Promise<any[]> {
  if (typeof window === 'undefined') return [];
  if (!portugueseWords) {
    try {
      const response = await fetch('/api/vocabulary/pt_v2.json');
      if (response.ok) {
        portugueseWords = await response.json();
        return portugueseWords!;
      }
    } catch {
      portugueseWords = [];
    }
  }
  return portugueseWords ?? [];
}

// Helper function to extract vocabulary by CEFR level with high-frequency words prioritized
function extractVocabularyByCEFR(data: any[], cefrLevel: string) {
  if (!Array.isArray(data)) {
    console.warn('Data is not an array:', typeof data);
    return [];
  }
  
  return data
    .filter((word: any) => 
      word.cefr_level === cefrLevel && 
      word.useful_for_flashcard
    )
    .sort((a: any, b: any) => (b.word_frequency || 0) - (a.word_frequency || 0)) // Sort by frequency (highest first)
    .map((word: any) => ({
      term: word.word,
      meaning: word.english_translation
    }));
}

// Function to get vocabulary data asynchronously
async function getVocabularyData() {
  const words = await loadPortugueseWords();
  return {
    A1: extractVocabularyByCEFR(words, "A1"), // 214 words - all available
    A2: extractVocabularyByCEFR(words, "A2"), // 861 words - all available
    B1: extractVocabularyByCEFR(words, "B1"), // 2,728 words - all available
    B2: extractVocabularyByCEFR(words, "B2"), // 3,285 words - all available
    C1: extractVocabularyByCEFR(words, "C1"), // 1,012 words - all available
    C2: extractVocabularyByCEFR(words, "C2")  // 29 words - all available
  };
}

export const portugueseContent: LanguageContent = {
  language: "Portuguese",
  code: "pt",
  pronouns: [
    "eu",
    "tu", 
    "ele",
    "ela",
    "você",
    "nós",
    "vós",
    "eles",
    "elas",
    "vocês"
  ],
  tenses: ["present", "preterite", "imperfect", "future", "conditional"],
  grammar: [
    {
      title: "Regular -ar verbs in present",
      explanation:
        "To conjugate -ar verbs, drop -ar and add: -o, -as, -a, -amos, -ais, -am.",
      examples: [
        "falar → eu falo, tu falas, ele/ela fala, nós falamos, vós falais, eles/elas falam",
      ],
    },
    {
      title: "Regular -er verbs in present",
      explanation:
        "Drop -er and add: -o, -es, -e, -emos, -eis, -em.",
      examples: ["comer → eu como, tu comes, ele come, nós comemos, vós comeis, eles comem"],
    },
    {
      title: "Regular -ir verbs in present",
      explanation:
        "Drop -ir and add: -o, -es, -e, -imos, -is, -em.",
      examples: ["viver → eu vivo, tu vives, ele vive, nós vivemos, vós viveis, eles vivem"],
    },
    {
      title: "Gender and number",
      explanation:
        "Most nouns ending in -o are masculine, -a feminine. Plural usually adds -s.",
      examples: ["o cão / os cães", "a casa / as casas", "o lápis / os lápis"],
    },
    {
      title: "Definite articles",
      explanation: "o (m.sg), a (f.sg), os (m.pl), as (f.pl).",
      examples: ["o livro, a mesa, os carros, as flores"],
    },
  ],
  vocabulary: async () => {
    const data = await getVocabularyData();
    return [
      {
        title: "A1 - Beginner",
        words: data.A1,
      },
      {
        title: "A2 - Elementary",
        words: data.A2,
      },
      {
        title: "B1 - Intermediate",
        words: data.B1,
      },
      {
        title: "B2 - Upper Intermediate",
        words: data.B2,
      },
      {
        title: "C1 - Advanced",
        words: data.C1,
      },
      {
        title: "C2 - Proficient",
        words: data.C2,
      },
    ];
  },
  sentences: [
    { prompt: "Eu __ café de manhã (beber)", answer: "bebo" },
    { prompt: "Nós __ em Lisboa (viver)", answer: "vivemos" },
    { prompt: "__ tu português? (falar)", answer: "falas" },
  ],
  comprehension: {
    paragraph:
      "Sofia vive em Lisboa. Todos os dias pega o metro para ir ao trabalho. Gosta de ler um livro durante a viagem e tomar um café quando chega ao escritório.",
    questions: [
      { q: "Onde vive Sofia?", a: "Em Lisboa." },
      { q: "Como vai ao trabalho?", a: "De metro." },
      { q: "O que gosta de fazer durante a viagem?", a: "Ler um livro." },
    ],
  },
  speaking: [
    {
      topic: "Apresentar-se",
      instruction: "Apresenta-te em 3-4 frases: nome, origem, trabalho/estudos, hobbies.",
      starter: "Olá, chamo-me... Sou de... Trabalho/Estudo... Gosto de...",
    },
    {
      topic: "Num café",
      instruction: "Imagina que pedes num café. Diz o que queres e pergunta o preço.",
    },
  ],
  verbs: [
    {
      infinitive: "falar",
      english: "to speak",
      conjugations: {
        present: {
          eu: "falo",
          tu: "falas",
          ele: "fala",
          ela: "fala",
          você: "fala",
          nós: "falamos",
          vós: "falais",
          eles: "falam",
          elas: "falam",
          vocês: "falam",
        },
      },
    },
    {
      infinitive: "comer",
      english: "to eat",
      conjugations: {
        present: {
          eu: "como",
          tu: "comes",
          ele: "come",
          ela: "come",
          você: "come",
          nós: "comemos",
          vós: "comeis",
          eles: "comem",
          elas: "comem",
          vocês: "comem",
        },
      },
    },
    {
      infinitive: "viver",
      english: "to live",
      conjugations: {
        present: {
          eu: "vivo",
          tu: "vives",
          ele: "vive",
          ela: "vive",
          você: "vive",
          nós: "vivemos",
          vós: "viveis",
          eles: "vivem",
          elas: "vivem",
          vocês: "vivem",
        },
      },
    },
    {
      infinitive: "ser",
      english: "to be (essential)",
      conjugations: {
        present: {
          eu: "sou",
          tu: "és",
          ele: "é",
          ela: "é",
          você: "é",
          nós: "somos",
          vós: "sois",
          eles: "são",
          elas: "são",
          vocês: "são",
        },
      },
    },
    {
      infinitive: "estar",
      english: "to be (state)",
      conjugations: {
        present: {
          eu: "estou",
          tu: "estás",
          ele: "está",
          ela: "está",
          você: "está",
          nós: "estamos",
          vós: "estais",
          eles: "estão",
          elas: "estão",
          vocês: "estão",
        },
      },
    },
    {
      infinitive: "ter",
      english: "to have",
      conjugations: {
        present: {
          eu: "tenho",
          tu: "tens",
          ele: "tem",
          ela: "tem",
          você: "tem",
          nós: "temos",
          vós: "tendes",
          eles: "têm",
          elas: "têm",
          vocês: "têm",
        },
      },
    },
    {
      infinitive: "ir",
      english: "to go",
      conjugations: {
        present: {
          eu: "vou",
          tu: "vais",
          ele: "vai",
          ela: "vai",
          você: "vai",
          nós: "vamos",
          vós: "ides",
          eles: "vão",
          elas: "vão",
          vocês: "vão",
        },
      },
    },
    {
      infinitive: "poder",
      english: "to be able to",
      conjugations: {
        present: {
          eu: "posso",
          tu: "podes",
          ele: "pode",
          ela: "pode",
          você: "pode",
          nós: "podemos",
          vós: "podeis",
          eles: "podem",
          elas: "podem",
          vocês: "podem",
        },
      },
    },
  ],
};
